import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import type { Theme } from 'hephaestus/core';
import { Box, Typography } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { sortFlagNotesNewestFirst } from '../utils/reconFlagNotes';
import type { ReconFlagRow } from '../utils/reconFlags.types';
import { formatDateTimeInEastern } from './reconFlagDrawerOverviewFormatting';
export type ReconFlagsDrawerNotesHistoryProps = {
  theme: Theme;
  flag: ReconFlagRow;
  t: TFunction;
  /** When false, empty state copy reflects that notes cannot be added. */
  canComposeNotes?: boolean;
};

export const ReconFlagsDrawerNotesHistory = ({
  theme,
  flag,
  t,
  canComposeNotes = true,
}: ReconFlagsDrawerNotesHistoryProps) => {
  const history = useMemo(
    () => sortFlagNotesNewestFirst(flag.notes),
    [flag.notes],
  );

  if (history.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 1,
          textAlign: 'center',
        }}
      >
        <SpeakerNotesOutlinedIcon
          color='disabled'
          sx={{ fontSize: 48 }}
          aria-hidden
        />
        <Typography variant='titleMedium'>
          {t(
            canComposeNotes
              ? 'reconFlags.drawer.notesHistoryEmptyTitle'
              : 'reconFlags.drawer.notesHistoryEmptyReadOnlyTitle',
          )}
        </Typography>
        {canComposeNotes && (
          <Typography
            variant='bodySmall'
            sx={{ color: theme.color.onSurfaceVariant, maxWidth: 280 }}
          >
            {t('reconFlags.drawer.notesHistoryEmptyDescription')}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {history.map((entry) => {
        const authorLabel = entry.author?.trim();

        return (
          <Box
            key={`${entry.createdAt}|${entry.authorId}|${entry.text}`}
            sx={{
              border: `1px solid ${String(theme.color.outlineVariant)}`,
              borderRadius: 1,
              p: 1.5,
              backgroundColor: String(
                theme.color.surfaceContainer ?? theme.color.surface,
              ),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
                mb: 0.75,
              }}
            >
              {authorLabel && (
                <>
                  <Typography
                    variant='labelSmall'
                    component='div'
                    sx={{ fontWeight: 600 }}
                  >
                    {authorLabel}
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    component='div'
                    sx={{ color: theme.color.onSurfaceVariant }}
                    aria-hidden
                  >
                    ·
                  </Typography>
                </>
              )}
              <Typography
                variant='labelSmall'
                sx={{ color: theme.color.onSurfaceVariant }}
              >
                {formatDateTimeInEastern(entry.createdAt)}
              </Typography>
            </Box>
            <Typography variant='bodySmall' sx={{ whiteSpace: 'pre-wrap' }}>
              {entry.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
